/**
 * MainMenuPanel - Full-screen creepy main menu
 */

import { UIPanel } from './UIPanel';
import { Vector2 } from '../math/Vector2';
import { Rectangle } from '../math/Rectangle';
import { SaveSlotInfo } from '../save/SaveManager';
import { t, Localization } from '../systems/Localization';
import { renderLogo } from './Logo';

export class MainMenuPanel extends UIPanel {
  private hasContinue: boolean = false;
  private lastSaveInfo: SaveSlotInfo | null = null;
  private hoveredButton: string | null = null;
  private animationTime: number = 0;
  private screenWidth: number = 1280;
  private screenHeight: number = 720;

  // Callbacks
  public onContinue: (() => void) | null = null;
  public onNewGame: (() => void) | null = null;
  public onLoadGame: (() => void) | null = null;

  private buttons: { id: string; label: string; y: number; enabled: boolean }[] = [];

  // Fog particles for creepy effect
  private fogParticles: { x: number; y: number; size: number; speed: number; opacity: number }[] = [];

  constructor() {
    super(0, 0, 1280, 720);
    this.title = '';
    this.isDraggable = false;
    this.backgroundColor = 'transparent';
    this.initFogParticles();
  }

  private initFogParticles(): void {
    // Create fog particles for atmospheric effect
    for (let i = 0; i < 50; i++) {
      this.fogParticles.push({
        x: Math.random() * 1920,
        y: Math.random() * 1080,
        size: 50 + Math.random() * 150,
        speed: 0.2 + Math.random() * 0.5,
        opacity: 0.03 + Math.random() * 0.07
      });
    }
  }

  setContinueAvailable(available: boolean, saveInfo?: SaveSlotInfo): void {
    this.hasContinue = available;
    this.lastSaveInfo = saveInfo ?? null;
    this.updateButtons();
  }

  show(): void {
    super.show();
    this.updateButtons();
  }

  setScreenSize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;
    this.bounds = new Rectangle(0, 0, width, height);
    this.updateButtons();
  }

  centerOnScreen(screenWidth: number, screenHeight: number): void {
    this.setScreenSize(screenWidth, screenHeight);
  }

  private updateButtons(): void {
    const buttonSpacing = 60;

    // Buttons start below the logo
    const startY = this.screenHeight * 0.55;

    this.buttons = [];

    if (this.hasContinue) {
      this.buttons.push({
        id: 'continue',
        label: t('menu_continue').toUpperCase(),
        y: startY,
        enabled: true
      });
    }

    this.buttons.push({
      id: 'newgame',
      label: t('menu_new_game').toUpperCase(),
      y: startY + (this.hasContinue ? buttonSpacing : 0),
      enabled: true
    });

    this.buttons.push({
      id: 'loadgame',
      label: t('menu_load_game').toUpperCase(),
      y: startY + (this.hasContinue ? buttonSpacing * 2 : buttonSpacing),
      enabled: true
    });

    // Language button
    this.buttons.push({
      id: 'language',
      label: `${t('menu_language')}: ${Localization.getLanguageName()}`,
      y: startY + (this.hasContinue ? buttonSpacing * 3 : buttonSpacing * 2),
      enabled: true
    });
  }

  update(deltaTime: number): void {
    this.animationTime += deltaTime;

    // Update fog particles
    for (const particle of this.fogParticles) {
      particle.x += particle.speed;
      if (particle.x > this.screenWidth + particle.size) {
        particle.x = -particle.size;
        particle.y = Math.random() * this.screenHeight;
      }
    }
  }

  protected onPanelClick(pos: Vector2): boolean {
    const centerX = this.screenWidth / 2;
    const buttonWidth = 280;
    const buttonHeight = 50;

    for (const button of this.buttons) {
      if (!button.enabled) continue;

      const buttonX = centerX - buttonWidth / 2;

      if (pos.x >= buttonX && pos.x <= buttonX + buttonWidth &&
          pos.y >= button.y && pos.y <= button.y + buttonHeight) {
        switch (button.id) {
          case 'continue':
            this.onContinue?.();
            break;
          case 'newgame':
            this.onNewGame?.();
            break;
          case 'loadgame':
            this.onLoadGame?.();
            break;
          case 'language':
            Localization.toggleLanguage();
            this.updateButtons();
            break;
        }
        return true;
      }
    }
    return true;
  }

  handleMouseMove(pos: Vector2): boolean {
    if (!this.isVisible) {
      this.hoveredButton = null;
      return false;
    }

    const centerX = this.screenWidth / 2;
    const buttonWidth = 280;
    const buttonHeight = 50;

    this.hoveredButton = null;
    for (const button of this.buttons) {
      if (!button.enabled) continue;

      const buttonX = centerX - buttonWidth / 2;

      if (pos.x >= buttonX && pos.x <= buttonX + buttonWidth &&
          pos.y >= button.y && pos.y <= button.y + buttonHeight) {
        this.hoveredButton = button.id;
        break;
      }
    }

    return true;
  }

  render(ctx: CanvasRenderingContext2D): void {
    if (!this.isVisible) return;

    // Dark gradient background
    const gradient = ctx.createLinearGradient(0, 0, 0, this.screenHeight);
    gradient.addColorStop(0, '#0a0a12');
    gradient.addColorStop(0.5, '#12121f');
    gradient.addColorStop(1, '#080810');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, this.screenWidth, this.screenHeight);

    // Render fog particles for creepy atmosphere
    this.renderFog(ctx);

    // Vignette effect
    this.renderVignette(ctx);

    // Render large logo with glow
    this.renderCreepyLogo(ctx);

    // Game subtitle
    ctx.fillStyle = '#4a4a5a';
    ctx.font = '16px monospace';
    ctx.textAlign = 'center';
    ctx.fillText('ISOMETRIC SURVIVAL', this.screenWidth / 2, this.screenHeight * 0.42);

    // Render continue save info if available
    if (this.hasContinue && this.lastSaveInfo) {
      ctx.fillStyle = '#666';
      ctx.font = '12px monospace';
      ctx.textAlign = 'center';
      const info = `Last: ${this.lastSaveInfo.name} - ${t('hud_day')} ${this.lastSaveInfo.day}`;
      ctx.fillText(info, this.screenWidth / 2, this.screenHeight * 0.52);
    }

    // Render buttons
    for (const button of this.buttons) {
      this.renderButton(ctx, button);
    }

    // Version/credits at bottom
    ctx.fillStyle = '#333';
    ctx.font = '11px monospace';
    ctx.textAlign = 'center';
    ctx.fillText('v1.0', this.screenWidth / 2, this.screenHeight - 20);
  }

  private renderFog(ctx: CanvasRenderingContext2D): void {
    for (const particle of this.fogParticles) {
      const gradient = ctx.createRadialGradient(
        particle.x, particle.y, 0,
        particle.x, particle.y, particle.size
      );
      gradient.addColorStop(0, `rgba(30, 40, 50, ${particle.opacity})`);
      gradient.addColorStop(1, 'rgba(30, 40, 50, 0)');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private renderVignette(ctx: CanvasRenderingContext2D): void {
    const centerX = this.screenWidth / 2;
    const centerY = this.screenHeight / 2;
    const radius = Math.max(this.screenWidth, this.screenHeight) * 0.8;

    const gradient = ctx.createRadialGradient(
      centerX, centerY, radius * 0.3,
      centerX, centerY, radius
    );
    gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
    gradient.addColorStop(0.7, 'rgba(0, 0, 0, 0.3)');
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0.8)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, this.screenWidth, this.screenHeight);
  }

  private renderCreepyLogo(ctx: CanvasRenderingContext2D): void {
    const centerX = this.screenWidth / 2;
    const logoY = this.screenHeight * 0.18;
    const logoScale = 10;

    // Eerie glow behind logo
    const glowPulse = Math.sin(this.animationTime * 2) * 0.2 + 0.8;
    for (let i = 3; i > 0; i--) {
      const glowSize = i * 8;
      ctx.shadowColor = `rgba(78, 204, 163, ${0.1 * glowPulse / i})`;
      ctx.shadowBlur = glowSize;
      renderLogo(ctx, centerX, logoY, logoScale, `rgba(78, 204, 163, ${0.05 / i})`);
    }
    ctx.shadowBlur = 0;

    // Main logo with slight color shift
    const r = 78 + Math.sin(this.animationTime * 1.5) * 10;
    const g = 204 + Math.sin(this.animationTime * 2) * 15;
    const b = 163 + Math.sin(this.animationTime * 1.8) * 10;
    renderLogo(ctx, centerX, logoY, logoScale, `rgb(${r}, ${g}, ${b})`);

    // Scanline effect over logo
    ctx.fillStyle = 'rgba(0, 0, 0, 0.03)';
    for (let y = logoY; y < logoY + 120; y += 3) {
      ctx.fillRect(centerX - 150, y, 300, 1);
    }
  }

  private renderButton(
    ctx: CanvasRenderingContext2D,
    button: { id: string; label: string; y: number; enabled: boolean }
  ): void {
    const centerX = this.screenWidth / 2;
    const buttonWidth = 280;
    const buttonHeight = 50;
    const buttonX = centerX - buttonWidth / 2;
    const isHovered = this.hoveredButton === button.id;

    // Button glow on hover
    if (isHovered) {
      ctx.shadowColor = this.getButtonColor(button.id);
      ctx.shadowBlur = 20;
    }

    // Button background - darker, more muted colors
    ctx.fillStyle = isHovered
      ? this.getButtonColor(button.id)
      : this.getButtonColorDark(button.id);
    ctx.fillRect(buttonX, button.y, buttonWidth, buttonHeight);

    // Button border
    ctx.strokeStyle = isHovered ? '#fff' : '#444';
    ctx.lineWidth = isHovered ? 2 : 1;
    ctx.strokeRect(buttonX, button.y, buttonWidth, buttonHeight);

    ctx.shadowBlur = 0;

    // Button text
    ctx.fillStyle = isHovered ? '#fff' : '#aaa';
    ctx.font = `bold ${isHovered ? '15' : '14'}px monospace`;
    ctx.textAlign = 'center';
    ctx.fillText(button.label, centerX, button.y + 31);
  }

  private getButtonColor(id: string): string {
    switch (id) {
      case 'continue': return '#4ecca3';
      case 'newgame': return '#3498db';
      case 'language': return '#8e44ad';
      default: return '#555';
    }
  }

  private getButtonColorDark(id: string): string {
    switch (id) {
      case 'continue': return 'rgba(78, 204, 163, 0.2)';
      case 'newgame': return 'rgba(52, 152, 219, 0.2)';
      case 'language': return 'rgba(142, 68, 173, 0.2)';
      default: return 'rgba(85, 85, 85, 0.3)';
    }
  }

  // Override to prevent closing with X button
  protected isCloseButtonClick(_pos: Vector2): boolean {
    return false;
  }

  // Don't render close button
  protected renderCloseButton(_ctx: CanvasRenderingContext2D): void {
    // No close button for main menu
  }

  // Override standard panel rendering
  protected renderContent(_ctx: CanvasRenderingContext2D): void {
    // Content is rendered in main render() method
  }

  protected renderTitleBar(_ctx: CanvasRenderingContext2D): void {
    // No title bar for full-screen menu
  }

  protected renderBackground(_ctx: CanvasRenderingContext2D): void {
    // Background is rendered in main render() method
  }
}
